<template>
    <!-- Guests: the first play asks for an account -->
    <section v-if="loginPromptTitle" class="fixed inset-0 z-[80] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center"
        role="dialog" aria-modal="true" aria-labelledby="login-prompt-title" @click.self="dismissLoginPrompt">
        <div class="w-full max-w-sm rounded-t-3xl bg-nomad-900 px-6 pb-8 pt-8 text-center text-white animate-slide-up sm:rounded-3xl">
            <img src="/favicon-96x96.png" alt="" class="mx-auto h-16 w-16 rounded-2xl" />
            <h2 id="login-prompt-title" class="mt-6 text-2xl font-bold tracking-tight">Log in to listen</h2>
            <p class="mt-2 leading-relaxed text-nomad-300">
                Play {{ loginPromptTitle }} and every song, podcast and audiobook on Heesify. It's free and takes one tap.
            </p>
            <button type="button" @click="logIn" class="nl-btn-primary mt-6 w-full !py-4 !text-base">
                Continue with Google
            </button>
            <button type="button" @click="dismissLoginPrompt"
                class="mt-3 py-2 text-sm font-medium text-nomad-400 transition-colors hover:text-white">
                Not now
            </button>
        </div>
    </section>

    <!-- Mobile: after today's free song, further plays point to the app -->
    <section v-if="appDownloadPlatform" class="fixed inset-0 z-[80] flex flex-col bg-black text-white animate-slide-up"
        role="dialog" aria-modal="true" aria-labelledby="app-prompt-title">
        <div class="flex justify-end p-4">
            <button type="button" @click="dismissAppPrompt"
                class="flex h-10 w-10 items-center justify-center rounded-full text-xl text-nomad-300 transition-colors hover:text-white"
                aria-label="Close">
                <span class="icon-x" aria-hidden="true"></span>
            </button>
        </div>
        <div class="mx-auto flex w-full max-w-sm flex-1 flex-col items-center justify-center px-6 pb-16 text-center">
            <img src="/favicon-96x96.png" alt="" class="h-20 w-20 rounded-2xl" />
            <h2 id="app-prompt-title" class="mt-8 text-2xl font-bold tracking-tight">Keep listening on the Heesify app</h2>
            <p class="mt-3 leading-relaxed text-nomad-300">
                You've played today's free song. Play {{ appDownloadTitle }} and every other song, podcast and audiobook
                in the free app.
            </p>
            <a :href="appDownloadUrl" target="_blank" rel="noopener noreferrer" @click="trackAppPrompt('click')"
                class="nl-btn-primary mt-8 w-full !py-4 !text-base">
                {{ appDownloadPlatform === 'android' ? 'Get it on Google Play' : 'Download on the App Store' }}
            </a>
            <button type="button" @click="dismissAppPrompt"
                class="mt-4 py-2 text-sm font-medium text-nomad-400 transition-colors hover:text-white">
                Not now
            </button>
            <p class="mt-8 text-xs text-nomad-500">You'll get another free song here tomorrow.</p>
        </div>
    </section>

    <div v-else-if="currentSong && currentSong.id" class="fixed inset-x-0 bottom-16 z-40 lg:bottom-0" :key="currentSong.id" data-player-dock>
        <Affiliates class="mb-2" />

        <!-- Player bar -->
        <div class="relative z-50 px-2 pb-2">
            <div
                class="relative overflow-hidden rounded-xl bg-nomad-800 text-white shadow-lift lg:bg-nomad-850 lg:ring-1 lg:ring-white/[0.06]">

                <!-- Compact progress for small screens -->
                <div class="absolute inset-x-0 top-0 h-0.5 bg-white/10 md:hidden">
                    <div class="h-full bg-nomad-accent transition-[width] duration-150" :style="{ width: progressPct + '%' }"></div>
                </div>

                <div class="relative grid h-14 grid-cols-[1fr_auto] items-center gap-3 px-2 md:h-20 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,1fr)] md:px-4">
                    <!-- Now playing -->
                    <div class="flex min-w-0 items-center gap-3">
                        <button type="button" @click="openFull('lyrics')"
                            class="group relative h-10 w-10 shrink-0 overflow-hidden rounded md:h-14 md:w-14"
                            aria-label="Open now playing">
                            <img :src="artwork" :alt="currentSong.name" class="h-full w-full object-cover" />
                            <span v-if="loading"
                                class="absolute inset-0 flex items-center justify-center bg-black/60">
                                <span class="h-5 w-5 animate-spin rounded-full border-2 border-nomad-accent border-t-transparent"></span>
                            </span>
                            <span v-else
                                class="absolute inset-0 flex items-center justify-center bg-black/50 text-lg opacity-0 transition-opacity group-hover:opacity-100">
                                <span class="icon-maximize-2" aria-hidden="true"></span>
                            </span>
                        </button>
                        <div class="min-w-0">
                            <h1 class="m-0 truncate text-sm font-semibold leading-tight md:text-[0.9375rem]">
                                <Link :href="songUrl" class="hover:underline">{{ currentSong.name }}</Link>
                            </h1>
                            <p class="mt-0.5 flex items-center gap-2 truncate text-xs text-nomad-300">
                                <span v-if="isPlaying" class="nl-eq shrink-0"><span></span><span></span><span></span></span>
                                <span class="truncate">{{ currentSong.artist.name }}</span>
                            </p>
                        </div>
                    </div>

                    <!-- Transport + seek -->
                    <div class="flex items-center gap-1 md:flex-col md:gap-1.5">
                        <div class="flex items-center gap-1 md:gap-3">
                            <button type="button" @click="playPrevious" :disabled="!hasPrevious"
                                class="hidden h-9 w-9 items-center justify-center rounded-full text-lg text-nomad-200 transition-colors hover:text-white disabled:opacity-30 md:flex"
                                aria-label="Previous">
                                <span class="icon-skip-back" aria-hidden="true"></span>
                            </button>
                            <button type="button" @click="playPause"
                                class="flex h-10 w-10 items-center justify-center rounded-full bg-white text-lg text-black transition-transform hover:scale-105"
                                :aria-label="isPlaying ? 'Pause' : 'Play'">
                                <span :class="[isPlaying ? 'icon-pause' : 'icon-play translate-x-px']" aria-hidden="true"></span>
                            </button>
                            <button type="button" @click="playNext" :disabled="!hasNext"
                                class="hidden h-9 w-9 items-center justify-center rounded-full text-lg text-nomad-200 transition-colors hover:text-white disabled:opacity-30 md:flex"
                                aria-label="Next">
                                <span class="icon-skip-forward" aria-hidden="true"></span>
                            </button>
                        </div>
                        <div class="hidden w-full max-w-xl items-center gap-3 md:flex">
                            <span class="w-10 text-right text-[0.6875rem] tabular-nums text-nomad-300">{{ formatTime(displayElapsed) }}</span>
                            <input type="range" step="1" v-model.number="progress" min="0" max="1000"
                                class="nl-range" :style="{ '--nl-fill': progressPct + '%' }" aria-label="Seek"
                                @change="onSeek" @pointerdown="startDrag" @pointerup="canDrag = false" />
                            <span class="w-10 text-[0.6875rem] tabular-nums text-nomad-300">{{ formatTime(duration) }}</span>
                        </div>
                    </div>

                    <!-- Actions -->
                    <div class="hidden items-center justify-end gap-1 md:flex">
                        <button v-if="lyricsHtml" type="button" @click="openFull('lyrics')"
                            class="flex h-9 w-9 items-center justify-center rounded-full text-lg text-nomad-200 transition-colors hover:bg-white/[0.06] hover:text-nomad-accent"
                            aria-label="Lyrics" title="Lyrics">
                            <span class="icon-file-text" aria-hidden="true"></span>
                        </button>
                        <button type="button" @click="togglePlaylist"
                            class="flex h-9 w-9 items-center justify-center rounded-full text-lg transition-colors hover:bg-white/[0.06]"
                            :class="showPlaylist ? 'text-nomad-accent' : 'text-nomad-200 hover:text-nomad-accent'"
                            aria-label="Recently played" title="Recently played">
                            <span class="icon-list" aria-hidden="true"></span>
                        </button>
                        <button type="button" @click="openFull('lyrics')"
                            class="ml-1 flex h-9 w-9 items-center justify-center rounded-full text-lg text-nomad-200 transition-colors hover:bg-white/[0.06] hover:text-white"
                            aria-label="Expand player" title="Expand">
                            <span class="icon-chevron-up" aria-hidden="true"></span>
                        </button>
                    </div>
                    <div class="flex items-center md:hidden">
                        <button type="button" @click="togglePlaylist"
                            class="flex h-10 w-10 items-center justify-center rounded-full text-lg text-nomad-200"
                            aria-label="Recently played">
                            <span class="icon-list" aria-hidden="true"></span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Recently played drawer -->
        <Transition enter-active-class="transition duration-300 ease-out" enter-from-class="opacity-0"
            leave-active-class="transition duration-200 ease-in" leave-to-class="opacity-0">
            <div v-if="showPlaylist" class="fixed inset-0 z-40 bg-black/30" @click="showPlaylist = false"></div>
        </Transition>
        <Transition enter-active-class="transition duration-300 ease-out" enter-from-class="translate-x-full opacity-0"
            leave-active-class="transition duration-200 ease-in" leave-to-class="translate-x-full opacity-0">
            <aside v-if="showPlaylist"
                class="fixed bottom-[8.5rem] right-2 top-2 z-50 flex w-[calc(100%-1rem)] max-w-sm flex-col overflow-hidden rounded-xl bg-nomad-850 shadow-lift lg:bottom-24"
                aria-label="Recently played">
                <header class="flex items-center justify-between border-b border-white/[0.06] px-5 py-4">
                    <div>
                        <h3 class="text-base font-semibold text-white">Recently played</h3>
                    </div>
                    <div class="flex items-center gap-1">
                        <button @click="clearHistory" v-if="playedSongs && playedSongs.length > 0" type="button"
                            class="rounded-full px-3 py-1.5 text-xs font-medium text-nomad-300 transition-colors hover:bg-white/[0.06] hover:text-red-400">
                            Clear
                        </button>
                        <button @click="showPlaylist = false" type="button"
                            class="flex h-9 w-9 items-center justify-center rounded-full text-lg text-nomad-200 hover:bg-white/[0.06] hover:text-white"
                            aria-label="Close">
                            <span class="icon-x" aria-hidden="true"></span>
                        </button>
                    </div>
                </header>
                <div class="nl-scroll flex-1 overflow-y-auto p-2">
                    <PlayerHistory :items="playedSongs" :current-id="currentSong.id" :is-playing="isPlaying"
                        @play="playSongFromHistory" />
                </div>
            </aside>
        </Transition>

        <!-- Full now-playing view -->
        <Transition enter-active-class="transition duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
            enter-from-class="translate-y-full" leave-active-class="transition duration-300 ease-in"
            leave-to-class="translate-y-full">
            <section v-if="showFull" class="fixed inset-0 z-[60] flex flex-col overflow-hidden bg-black text-white"
                role="dialog" aria-modal="true" aria-label="Now playing">
                <img :src="artwork" alt="" aria-hidden="true"
                    class="pointer-events-none absolute inset-0 h-full w-full scale-125 object-cover opacity-40 blur-3xl" />
                <div class="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/30 via-black/70 to-black"></div>

                <header class="relative mx-auto flex w-full max-w-7xl items-center justify-between px-4 py-4 sm:px-8">
                    <button type="button" @click="showFull = false" class="nl-btn-icon" aria-label="Close now playing">
                        <span class="icon-chevron-down" aria-hidden="true"></span>
                    </button>
                    <p class="nl-eyebrow">Now playing</p>
                    <Link :href="songUrl" @click="showFull = false" class="nl-btn-icon" aria-label="Open song page">
                        <span class="icon-external-link text-base" aria-hidden="true"></span>
                    </Link>
                </header>

                <div
                    class="relative mx-auto grid min-h-0 w-full max-w-7xl flex-1 grid-cols-1 gap-8 overflow-y-auto px-4 pb-8 sm:px-8 lg:grid-cols-2 lg:gap-16 lg:overflow-hidden lg:pb-12">
                    <!-- Artwork + controls -->
                    <div class="nl-scroll flex flex-col items-center justify-center lg:min-h-0 lg:items-start lg:overflow-y-auto">
                        <img :src="artwork" :alt="currentSong.name"
                            class="aspect-square w-full max-w-[min(22rem,40vh)] rounded-3xl object-cover shadow-[0_40px_80px_-30px_rgba(0,0,0,0.9)] ring-1 ring-white/10 lg:max-w-[min(26rem,42vh)]" />
                        <div class="mt-6 w-full max-w-[26rem]">
                            <h2 class="font-display text-2xl font-bold leading-tight tracking-tight md:text-3xl">{{ currentSong.name }}</h2>
                            <p class="mt-1 text-nomad-200">{{ currentSong.artist.name }}</p>

                            <div class="mt-6">
                                <input type="range" step="1" v-model.number="progress" min="0" max="1000"
                                    class="nl-range" :style="{ '--nl-fill': progressPct + '%' }" aria-label="Seek"
                                    @change="onSeek" @pointerdown="startDrag" @pointerup="canDrag = false" />
                                <div class="mt-2 flex justify-between text-xs tabular-nums text-nomad-300">
                                    <span>{{ formatTime(displayElapsed) }}</span>
                                    <span>{{ formatTime(duration) }}</span>
                                </div>
                            </div>

                            <div class="mt-4 flex items-center justify-center gap-6">
                                <button type="button" @click="playPrevious" :disabled="!hasPrevious"
                                    class="flex h-12 w-12 items-center justify-center rounded-full text-2xl text-nomad-100 transition-colors hover:text-white disabled:opacity-30"
                                    aria-label="Previous">
                                    <span class="icon-skip-back" aria-hidden="true"></span>
                                </button>
                                <button type="button" @click="playPause" class="nl-play-fab h-16 w-16 text-2xl"
                                    :aria-label="isPlaying ? 'Pause' : 'Play'">
                                    <span v-if="loading" class="h-6 w-6 animate-spin rounded-full border-2 border-black border-t-transparent"></span>
                                    <span v-else :class="[isPlaying ? 'icon-pause' : 'icon-play translate-x-0.5']" aria-hidden="true"></span>
                                </button>
                                <button type="button" @click="playNext" :disabled="!hasNext"
                                    class="flex h-12 w-12 items-center justify-center rounded-full text-2xl text-nomad-100 transition-colors hover:text-white disabled:opacity-30"
                                    aria-label="Next">
                                    <span class="icon-skip-forward" aria-hidden="true"></span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Lyrics / history -->
                    <div class="flex min-h-[24rem] flex-col lg:min-h-0">
                        <div class="mb-4 inline-flex self-start rounded-full bg-white/[0.06] p-1 ring-1 ring-inset ring-white/10" role="tablist">
                            <button v-for="tab in fullTabs" :key="tab.id" type="button" role="tab"
                                :aria-selected="fullTab === tab.id" @click="fullTab = tab.id"
                                class="rounded-full px-4 py-1.5 text-sm font-medium transition-colors"
                                :class="fullTab === tab.id ? 'bg-white text-black' : 'text-nomad-200 hover:text-white'">
                                {{ tab.label }}
                            </button>
                        </div>
                        <div class="nl-scroll nl-mask-fade-b min-h-0 flex-1 overflow-y-auto pr-2">
                            <div v-if="fullTab === 'lyrics'">
                                <div v-if="lyricsHtml" v-html="lyricsHtml" dir="auto"
                                    class="whitespace-pre-line pb-16 font-display text-xl font-medium leading-relaxed text-white/90 md:text-2xl md:leading-relaxed">
                                </div>
                                <div v-else class="flex flex-col items-start gap-3 py-10">
                                    <p class="text-lg text-nomad-200">No lyrics for this one yet.</p>
                                    <Link :href="songUrl" @click="showFull = false" class="nl-btn-ghost">Add lyrics on the song page</Link>
                                </div>
                            </div>
                            <div v-else>
                                <div class="mb-2 flex items-center justify-between px-2">
                                    <span class="text-xs text-nomad-400">{{ playedSongs.length }} songs</span>
                                    <button v-if="playedSongs.length" type="button" @click="clearHistory"
                                        class="text-xs text-nomad-300 hover:text-red-400">Clear</button>
                                </div>
                                <PlayerHistory :items="playedSongs" :current-id="currentSong.id"
                                    :is-playing="isPlaying" @play="playSongFromHistory" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </Transition>
    </div>
</template>

<script setup>
import { Link } from "@inertiajs/vue3";
import {
    nextTick,
    onMounted,
    reactive,
    ref,
    watch
} from "vue";
import NomadPlayer from "@/Layouts/NomadPlayer.js";
import Affiliates from "@/components/Affiliates.vue";
import PlayerHistory from "@/components/PlayerHistory.vue";
const playerInst = ref();
const playState = ref(true);

onMounted(async () => {
    await nextTick();
    //initPlayer();
});

function initPlayer() {
    playerInst.value = new NomadPlayer(Math.random());
}

function setVolume() { }

watch(playerInst, (oldval, newval) => {
    //playerInst.value.playAudio()
});

defineExpose({
    /** @description get sound state */
    setVolume,
});
</script>
<script>
import {
    Howl,
    Howler
} from "howler";
import {
    inject,
    defineComponent
} from "vue";
import {
    mapActions,
    mapGetters,
    mapState
} from "vuex";

export default {
    data() {
        return {
            _howl: null,
            progress: 0,
            elapsed: 0,
            duration: 0,
            isPlaying: false,
            canDrag: false,
            loading: true,
            showPlaylist: false,
            showFull: false,
            fullTab: "lyrics",
            fullTabs: [
                { id: "lyrics", label: "Lyrics" },
                { id: "history", label: "Recently played" },
            ],
        };
    },
    created() {

    },
    mounted() {
        // Load played songs from localStorage when component mounts
        this.$store.dispatch('current/loadPlayedSongs');

        window.addEventListener("keydown", this.onKeydown);
    },
    beforeUnmount() {
        window.removeEventListener("keydown", this.onKeydown);
    },
    computed: {
        currentSong() {
            return this.$store.getters["current/currentSong"];
        },
        loginPromptTitle() {
            return this.$store.state.current.loginPromptTitle;
        },
        appDownloadPlatform() {
            return this.$store.state.current.appDownloadPlatform;
        },
        appDownloadTitle() {
            return this.$store.state.current.appDownloadTitle || 'your selection';
        },
        appDownloadUrl() {
            return this.appDownloadPlatform === 'android'
                ? 'https://play.google.com/store/apps/details?id=com.nomadlyrics'
                : 'https://apps.apple.com/app/apple-store/id1503789749?pt=121219390&ct=NomadLyricsWebsite&mt=8';
        },
        recentlyPlayed() {
            return this.$store.getters["current/recentlyPlayed"];
        },
        playedSongs() {
            return this.$store.getters["current/playedSongs"];
        },
        artwork() {
            return this.currentSong.cover || this.currentSong.artist?.image;
        },
        songUrl() {
            return this.currentSong.content_type == 'EPISODE'
                ? '/episode/' + this.currentSong.id
                : '/song/' + this.currentSong.id;
        },
        lyricsHtml() {
            const verse = this.currentSong.verse;
            return (verse && verse.length && verse[0].lyrics?.non_synced_lyrics?.original) || '';
        },
        progressPct() {
            return Math.min(Math.max(this.progress / 10, 0), 100);
        },
        displayElapsed() {
            // While dragging, preview the time under the thumb
            return this.canDrag ? (this.progress / 1000) * this.duration : this.elapsed;
        },
        historyIndex() {
            return this.playedSongs.findIndex(song => song.id === this.currentSong.id);
        },
        hasNext() {
            return this.historyIndex !== -1 && this.historyIndex < this.playedSongs.length - 1;
        },
        hasPrevious() {
            return this.historyIndex > 0;
        },
    },
    methods: {
        ...mapActions("current", ["prepareCurrent", "clearPlayedSongs"]),
        logIn() {
            this.trackLoginPrompt('click');
            this.$store.dispatch('user/login');
        },
        dismissLoginPrompt() {
            this.$store.commit('current/HIDE_LOGIN_PROMPT');
        },
        trackLoginPrompt(action) {
            this.$gtag.event(action === 'click' ? 'login_prompt_click' : 'login_prompt_view', {
                event_category: 'first_listen',
            });
        },
        dismissAppPrompt() {
            this.$store.commit('current/HIDE_APP_DOWNLOAD');
        },
        trackAppPrompt(action) {
            this.$gtag.event(action === 'click' ? 'app_prompt_click' : 'app_prompt_view', {
                event_category: 'mobile_free_play',
                platform: this.appDownloadPlatform,
            });
        },
        togglePlaylist() {
            this.showPlaylist = !this.showPlaylist;
        },
        openFull(tab) {
            this.fullTab = tab === 'lyrics' && !this.lyricsHtml ? 'history' : tab;
            this.showPlaylist = false;
            this.showFull = true;
        },
        clearHistory() {
            this.clearPlayedSongs();
        },
        playSongFromHistory(song) {
            // Play song from recently played history
            this.prepareCurrent({
                id: song.id,
                type: song.content_type || 'SONG',
                keepOrder: true,
            });
        },
        onKeydown(e) {
            if (e.key === "Escape") {
                if (this.loginPromptTitle) this.dismissLoginPrompt();
                if (this.appDownloadPlatform) this.dismissAppPrompt();
                this.showFull = false;
                this.showPlaylist = false;
                return;
            }

            const tag = (e.target && e.target.tagName) || "";
            const typing = ["INPUT", "TEXTAREA", "SELECT", "BUTTON"].includes(tag) || e.target?.isContentEditable;
            if (e.code === "Space" && !typing && this.currentSong && this.currentSong.id) {
                e.preventDefault();
                this.playPause();
            }
        },
        formatTime(seconds) {
            if (!seconds || !Number.isFinite(seconds)) return "0:00";
            const total = Math.floor(seconds);
            const h = Math.floor(total / 3600);
            const m = Math.floor((total % 3600) / 60);
            const s = String(total % 60).padStart(2, "0");
            return h ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
        },
        playNext() {
            // History is newest first, so "next" walks to the older entry
            if (!this.hasNext) return;
            this.playSongFromHistory(this.playedSongs[this.historyIndex + 1]);
        },
        playPrevious() {
            if (!this.hasPrevious) return;
            this.playSongFromHistory(this.playedSongs[this.historyIndex - 1]);
        },
        initPlayer() {
            this._howl = new Howl({
                src: this.currentSong.sources[0].src,
                html5: true,
                autoplay: false,
                preload: false,
                format: ["mp3", "mpeg"],
                onload: () => {
                    this.loading = false
                    this.duration = this._howl.duration() || 0
                },
                onloaderror: (e, msg) => {
                    console.log(msg);
                    this.loading = false
                },
                onplayerror: (e, msg) => {
                    console.log(msg);
                    this.loading = false
                },
                onplay: () => requestAnimationFrame(this.step.bind(this)),
                onend: () => {
                    this.stopPlay();
                },
                onpause: () => { },
                onrate: () => { },
                onstop: () => { },
                onseek: () => {
                    requestAnimationFrame(this.step.bind(this));
                },
                onfade: () => { },
                onunlock: () => { },
            });

            this._howl.on("play", () => {
                this.trackSongPlay(this.currentSong.content_type, this.currentSong.id, 'play')
                this.isPlaying = true;
            });


            this._howl.on("pause", () => {
                this.trackSongPlay(this.currentSong.content_type, this.currentSong.id, 'pause')
                this.isPlaying = false;
            });

            this._howl.on("load", () => {
                this.trackSongPlay(this.currentSong.content_type, this.currentSong.id, 'load')
                this._howl.play();
            });
        },
        onSeek(e) {
            this.canDrag = false;
            if (!this._howl) return;

            let sound = this._howl;
            var seekedTo = e.target.valueAsNumber;

            if (!sound.duration()) return;

            const seekPosition = (seekedTo / 1000) * sound.duration();
            sound.seek(seekPosition);
            this.elapsed = seekPosition;
        },
        startDrag() {
            this.canDrag = true;
        },
        trackSongPlay(type, id, eventCategory) {
            // Determine the event label based on the type (song or episode)
            const eventLabel = type === 'SONG' ? 'song_id' : 'episode_id';
            const eventType = type === 'SONG' ? 'song_play' : 'episode_play'

            // Track the "SongPlay" event with the associated ID
            this.$gtag.event(eventType, {
                event_category: eventCategory, // Event category (you can customize this)
                [eventLabel]: id,       // Use dynamic event label based on type
            });
        },
        mediaController() {
            if ("mediaSession" in navigator) {
                // Clear any existing media session first
                navigator.mediaSession.metadata = null;

                // Clear existing action handlers
                try {
                    navigator.mediaSession.setActionHandler("play", null);
                    navigator.mediaSession.setActionHandler("pause", null);
                } catch (error) {
                    console.log('Error clearing media session handlers:', error);
                }

                const thumbnail =
                    this.currentSong.cover || this.currentSong.artist.image;
                navigator.mediaSession.metadata = new MediaMetadata({
                    title: this.currentSong.name,
                    artist: this.currentSong.artist.name,
                    album: this.currentSong.artist.name,
                    artwork: [{
                        src: thumbnail,
                        sizes: "96x96",
                        type: "image/png",
                    },
                    {
                        src: thumbnail,
                        sizes: "128x128",
                        type: "image/png",
                    },
                    {
                        src: thumbnail,
                        sizes: "192x192",
                        type: "image/png",
                    },
                    {
                        src: thumbnail,
                        sizes: "256x256",
                        type: "image/png",
                    },
                    {
                        src: thumbnail,
                        sizes: "384x384",
                        type: "image/png",
                    },
                    {
                        src: thumbnail,
                        sizes: "512x512",
                        type: "image/png",
                    },
                    ],
                });

                navigator.mediaSession.setActionHandler("play", () => {
                    this.playPause();
                });
                navigator.mediaSession.setActionHandler("pause", () => {
                    this.playPause();
                });

                // Add playlist navigation
                navigator.mediaSession.setActionHandler("previoustrack", () => {
                    this.playPrevious();
                });

                navigator.mediaSession.setActionHandler("nexttrack", () => {
                    this.playNext();
                });
            }
        },
        play() {
            this.loading = true;
            this.elapsed = 0;
            this.duration = 0;
            this.progress = 0;
            let player = this._howl;

            if (player.playing()) {
                this._howl.stop();
            }

            this._howl.unload();

            if (this.currentSong.stream && (this.currentSong.stream == 'VOCAL' || this.currentSong.stream == 'MUSIC')) {
                if (this.currentSong.stream == 'VOCAL') {
                    this._howl._src = this.currentSong.smart.vocals;
                } else {
                    this._howl._src = this.currentSong.smart.music;
                }
            } else {
                this._howl._src = this.currentSong.sources[0].src;
            }


            this._howl.load();

            this.mediaController();
        },
        playPause() {
            if (!this._howl) {
                this.initPlayer();
                this.play(); // Initialize if no player exists
                return;
            }

            if (this._howl.playing()) {
                this._howl.pause();
            } else {
                // Check if it's paused vs stopped/unloaded
                if (this._howl.state() === 'loaded') {
                    this._howl.play(); // Resume from current position
                } else {
                    this.play(); // Restart if unloaded
                }
            }
        },
        stopPlay() {
            let player = this._howl;

            this._howl.stop();

            this._howl.unload();
            this.isPlaying = false;
            this.progress = 0
            this.elapsed = 0
        },
        step() {
            let sound = this._howl;
            if (!sound) return;
            let seek = sound.seek() || 0;
            if (typeof seek !== 'number') seek = 0;
            this.duration = sound.duration() || this.duration;
            if (!this.canDrag) {
                this.elapsed = seek;
                this.progress = (seek / sound.duration()) * 1000 || 0;
            }

            if (sound.playing()) requestAnimationFrame(this.step.bind(this));
        },
    },
    watch: {
        loginPromptTitle(title) {
            if (title) this.trackLoginPrompt('view');
        },
        appDownloadPlatform(platform) {
            if (platform) this.trackAppPrompt('view');
            if (platform && this._howl) {
                this._howl.stop();
                this._howl.unload();
                this._howl = null;
                this.isPlaying = false;
                this.progress = 0;
            }
        },
        currentSong: {
            handler(old, newval) {
                if (!this.currentSong || !this.currentSong.id) return;
                if (!this._howl) this.initPlayer();
                this.play();
            },
            deep: true,
        },
        isPlaying(playing) {
            this.$store.commit('current/SET_PLAYING', playing);
        },
        '$store.state.current.toggleTick'() {
            if (this.currentSong && this.currentSong.id) this.playPause();
        },
        showFull(open) {
            if (typeof document !== 'undefined') document.body.style.overflow = open ? 'hidden' : '';
        },
    },
};
</script>
